/**
 * ParentDailyStory — Alouvya's signature parent feature.
 *
 * Renders the AI-composed daily story for the parent's child, with calm
 * stats below (mood, rest, meals, activities). Designed mobile-first;
 * matches the existing parent-app design language.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

function DotChip({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[#EEF0F4] bg-white px-4 py-3"
    >
      <div className="eyebrow">{label}</div>
      <div className="mt-1 text-sm text-[#1A1B2E]">{value}</div>
    </div>
  );
}

function formatMinutes(min) {
  if (!min) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

export default function ParentDailyStory() {
  const { t } = useI18n();
  const { childId } = useParams();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/ops/me/daily-story/${childId}`);
        setLog(r.data);
      } catch (e) {
        if (e?.response?.status === 404) {
          setNotFound(true);
        }
      }
      setLoading(false);
    })();
  }, [childId]);

  if (loading) {
    return (
      <main className="max-w-2xl mx-auto p-6 pb-24">
        <p className="font-serif italic text-[#525252]">{t("ops.common.loading")}</p>
      </main>
    );
  }

  if (notFound || !log) {
    return (
      <main className="max-w-2xl mx-auto p-6 pb-24" data-testid="parent-daily-story-empty">
        <Link
          to="/home"
          className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#1A1B2E] mb-6"
        >
          <ArrowLeft size={14} strokeWidth={1.6} />
          {t("ops.parent_story.back_to_app")}
        </Link>
        <span className="eyebrow">{t("ops.parent_story.eyebrow")}</span>
        <h1 className="mt-2 font-display text-3xl text-[#1A1B2E]">
          {t("ops.parent_story.title")}
        </h1>
        <p className="mt-6 font-serif italic text-[#525252]">
          {t("ops.parent_story.no_story_yet")}
        </p>
      </main>
    );
  }

  // Stats
  const totalNap = (log.naps || []).reduce(
    (s, n) => s + (n.duration_minutes || 0),
    0
  );
  const moodSet = new Set((log.mood_entries || []).map((m) => m.mood));
  const moodSummary =
    moodSet.size > 0
      ? Array.from(moodSet)
          .slice(0, 3)
          .map((m) => t(`ops.mood.${m}`))
          .join(" · ")
      : "—";
  const totalWater = (log.water_intake || []).reduce(
    (s, w) => s + (w.ounces || 0),
    0
  );

  return (
    <main className="max-w-2xl mx-auto px-5 pt-6 pb-24" data-testid="parent-daily-story">
      <Link
        to="/home"
        className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#1A1B2E] mb-6"
        data-testid="parent-daily-story-back"
      >
        <ArrowLeft size={14} strokeWidth={1.6} />
        {t("ops.parent_story.back_to_app")}
      </Link>

      <span className="eyebrow">{t("ops.parent_story.eyebrow")}</span>
      <h1 className="mt-2 font-display text-3xl text-[#1A1B2E]">
        {log.child_name?.split(" ")[0]}'s day.
      </h1>

      <section
        className="mt-6 rounded-3xl bg-[#FAF9F1] border border-[#EEF0F4] p-6 lg:p-8 relative"
        data-testid="parent-daily-story-card"
      >
        {log.parent_story ? (
          <>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} strokeWidth={1.6} className="text-[#1F3A2A]" />
              <span className="eyebrow">Today</span>
            </div>
            <p
              className="font-serif text-lg text-[#1A1B2E] leading-relaxed whitespace-pre-line"
              data-testid="parent-daily-story-text"
            >
              {log.parent_story}
            </p>
            {log.parent_story_generated_at && (
              <p className="mt-4 text-xs text-[#9CA3AF]">
                {t("ops.parent_story.generated_at")}{" "}
                {log.parent_story_generated_at.slice(11, 16)}.
              </p>
            )}
          </>
        ) : (
          <p className="font-serif italic text-[#525252]">
            {t("ops.parent_story.no_story_yet")}
          </p>
        )}
      </section>

      <section className="mt-6 grid grid-cols-2 gap-3" data-testid="parent-daily-story-stats">
        <DotChip
          label={t("ops.parent_story.mood_summary")}
          value={moodSummary}
          testid="story-stat-mood"
        />
        <DotChip
          label={t("ops.parent_story.naps_summary")}
          value={formatMinutes(totalNap)}
          testid="story-stat-rest"
        />
        <DotChip
          label={t("ops.parent_story.meals_summary")}
          value={`${(log.meals || []).length} eaten`}
          testid="story-stat-meals"
        />
        <DotChip
          label={t("ops.parent_story.activities_summary")}
          value={`${(log.activities || []).length} today`}
          testid="story-stat-activities"
        />
      </section>

      {(log.activities || []).length > 0 && (
        <section className="mt-8" data-testid="parent-daily-story-activities">
          <h3 className="eyebrow">{t("ops.parent_story.activities_summary")}</h3>
          <ul className="mt-3 space-y-2">
            {log.activities.map((a) => (
              <li
                key={a.id}
                className="rounded-2xl border border-[#EEF0F4] bg-white px-4 py-3 text-sm text-[#1A1B2E]"
              >
                <div className="font-medium">{a.name}</div>
                <div className="text-xs text-[#6B7280] mt-0.5">
                  {t(`ops.activity_category.${a.category}`)}
                  {a.duration_minutes ? ` · ${a.duration_minutes}m` : ""}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
